/**
 * @tyndo/ui-kit/domain/OperationalModeBar
 *
 * Closes G2.6 from docs/latest-analysis.md by extracting
 * the Now/History/Explore mode + range-preset bar from the
 * Production canary into a reusable composite.
 *
 * Operational modules wrap their page in
 * `<ModeProvider scope="tyndo.X">` + `<TimeRangeProvider scope="tyndo.X">`
 * and render this bar near the page header. The bar reads + writes
 * the contexts; the dashboard's data layer reads `useMode()` /
 * `useTimeRange()` directly to switch live subscriptions on/off.
 *
 * Wire pattern at the call site:
 *
 * ```tsx
 * <ModeProvider scope="tyndo.scada">
 *   <TimeRangeProvider scope="tyndo.scada">
 *     <OperationalModeBar />
 *     <ScadaOverview />
 *   </TimeRangeProvider>
 * </ModeProvider>
 * ```
 */

import * as React from 'react';
import { useMode, useTimeRange } from '@tyndo/hooks';

const RANGE_PRESETS: Array<{ id: '1h' | '8h' | '24h' | '7d' | 'shift'; label: string }> = [
  { id: '1h', label: '1h' },
  { id: '8h', label: '8h' },
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
  { id: 'shift', label: 'Shift' },
];

const MODES: ReadonlyArray<'now' | 'history' | 'explore'> = ['now', 'history', 'explore'];

export interface OperationalModeBarProps {
  /** Optional className for outer container. */
  className?: string;
  /** Hide the time-range presets — useful when caller renders TimeSpine instead. */
  hideRangePresets?: boolean;
}

export const OperationalModeBar: React.FC<OperationalModeBarProps> = ({
  className,
  hideRangePresets = false,
}) => {
  const { mode, setMode } = useMode();
  const { range, setRange, isLive } = useTimeRange();

  return (
    <div
      className={[
        'mb-3 flex items-center gap-3 rounded border border-border bg-surface-base p-2',
        className ?? '',
      ].join(' ')}
    >
      <span className="t-eyebrow text-muted-foreground">Mode</span>
      <div role="radiogroup" className="inline-flex rounded-md border border-border bg-card p-0.5">
        {MODES.map(m => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={mode === m}
            onClick={() => setMode(m)}
            className={[
              'rounded px-2.5 py-1 text-sm capitalize',
              mode === m
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {m}
          </button>
        ))}
      </div>

      {!hideRangePresets && (
        <>
          <span className="t-eyebrow text-muted-foreground">Range</span>
          <div className="inline-flex flex-wrap gap-1">
            {RANGE_PRESETS.map(p => {
              const active = range.kind === 'relative' && range.window === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setRange({ kind: 'relative', window: p.id })}
                  className={[
                    'rounded px-2 py-1 text-xs',
                    active
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground',
                  ].join(' ')}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </>
      )}

      {isLive && (
        <span className="ml-auto inline-flex items-center gap-1 text-xs text-accent-live">
          <span className="inline-block h-1.5 w-1.5 animate-tyndo-phosphor-pulse rounded-full bg-accent-live" />
          Live
        </span>
      )}
    </div>
  );
};
